/* eslint-disable jsx-a11y/alt-text */
import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

export default function CarCarousel({ images = [] }) {
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({ axis: 'x' });

  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const syncThumbs = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;

    const index = emblaMainApi.selectedScrollSnap();
    setSelectedIndex(index);
    emblaThumbsApi.scrollTo(index);
  }, [emblaMainApi, emblaThumbsApi]);

  useEffect(() => {
    if (!emblaMainApi) return;

    syncThumbs();
    emblaMainApi.on('select', syncThumbs);
    emblaMainApi.on('reInit', syncThumbs);
  }, [emblaMainApi, syncThumbs]);

  const scrollPrev = useCallback(() => {
    emblaMainApi?.scrollPrev();
  }, [emblaMainApi]);

  const scrollNext = useCallback(() => {
    emblaMainApi?.scrollNext();
  }, [emblaMainApi]);

  const onThumbClick = useCallback(
    (index: number) => {
      emblaMainApi?.scrollTo(index);
    },
    [emblaMainApi]
  );

  return (
    <div className="w-full flex flex-col gap-4">
      {/* MAIN IMAGE */}
      <div className="overflow-hidden rounded-xl" ref={emblaMainRef}>
        <div className="flex">
          {images.map((img, index) => (
            <div key={index} className="min-w-full h-[320px] flex-shrink-0">
              <img src={img} className="w-full h-full object-cover rounded-xl" />
            </div>
          ))}
        </div>
      </div>

      {/* THUMBNAILS + BUTTONS */}
      <div className="w-full flex items-center gap-3 relative">
        <div
          className="absolute z-[1] h-[68.9px] w-[30px] flex items-center justify-center pl-[5px]
              bg-gradient-to-l from-black/40 via-black/5 to-transparent backdrop-blur-xs
                cursor-pointer select-none pointer-events-auto
                hover:from-black/30 hover:via-black/20 
                active:scale-95 transition-all duration-150 bg-black opacity-[0.7] rounded-l-[8px]"
          onClick={scrollPrev}
        >
          <LeftOutlined className="text-white text-[20px]" />
        </div>

        <div className="overflow-hidden w-full" ref={emblaThumbsRef}>
          <div className="flex gap-3 embla__container">
            {images.map((img, index) => (
              <div
                key={index}
                onClick={() => onThumbClick(index)}
                className={`embla__slide flex-shrink-0 snap-start w-[80px] h-[70px]
                    rounded-xl cursor-pointer overflow-hidden
                    border-2 transition-all duration-200
                    ${
                      selectedIndex === index
                        ? 'rounded-lg shadow-[0_0_0_2px_#5869FF]'
                        : 'border-gray-200 hover:border-gray-400 hover:shadow'
                    }`}
              >
                <img
                  src={img}
                  className={`
                  w-full h-full object-cover rounded-lg
                  transition-all duration-300 ease-out
                  ${selectedIndex === index ? 'scale-110' : 'scale-100'}
                `}
                />
              </div>
            ))}
          </div>
        </div>

        <div
          className="absolute z-[1] h-full w-[30px] flex items-center justify-center right-0 pr-[5px]
              bg-gradient-to-l from-black/40 via-black/5 to-transparent backdrop-blur-xs
            cursor-pointer select-none pointer-events-auto
            hover:from-black/30 hover:via-black/10 
            active:scale-95 transition-all duration-150 bg-black opacity-[0.7] rounded-r-[8px]"
          onClick={scrollNext}
        >
          <RightOutlined className="text-white text-[20px]" />
        </div>
        {/* <button
          onClick={scrollNext}
          className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full"
        >
          <RightOutlined />
        </button> */}
      </div>
    </div>
  );
}
